import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { products, categories, Product } from '@/mocks/products';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useCart } from '@/hooks/useCart';
import ReviewModal from './components/ReviewModal';

function StarRating({ rating, reviewCount, onClickReviews }: { rating: number; reviewCount: number; onClickReviews: (e: React.MouseEvent) => void }) {
  return (
    <button
      onClick={onClickReviews}
      className="flex items-center gap-1.5 cursor-pointer group/stars"
      title="See all reviews"
    >
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => {
          const filled = star <= Math.floor(rating);
          const partial = !filled && star === Math.ceil(rating) && rating % 1 !== 0;
          return (
            <span key={star} className={`text-xs ${filled || partial ? 'text-amber-400' : 'text-stone-200'}`}>
              <i className={filled ? 'ri-star-fill' : partial ? 'ri-star-half-fill' : 'ri-star-line'} />
            </span>
          );
        })}
      </div>
      <span className="text-[10px] text-stone-400 group-hover/stars:text-[#8B6348] transition-colors">
        {rating.toFixed(1)} ({reviewCount})
      </span>
    </button>
  );
}

export default function ProductsPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [reviewProduct, setReviewProduct] = useState<Product | null>(null);
  const { addItem } = useCart();
  const navigate = useNavigate();

  const filtered = activeCategory === 'All'
    ? products
    : products.filter((p) => p.category === activeCategory);

  return (
    <div>
      <Navbar />

      {/* Page Header */}
      <section className="w-full bg-[#f5f0ea] pt-36 pb-16 px-6 md:px-16">
        <p className="text-xs uppercase tracking-[0.3em] text-[#8B6348] mb-3 font-medium">Our Range</p>
        <h1 className="text-5xl md:text-6xl font-bold text-[#2c2420]" style={{ fontFamily: "'Playfair Display', serif" }}>
          All Products
        </h1>
        <p className="text-stone-500 mt-4 max-w-lg text-base leading-relaxed">
          Explore our complete range of handcrafted sofa beds — each piece designed for beauty and built to last.
        </p>
      </section>

      {/* Filter Bar */}
      <section className="w-full bg-white border-b border-stone-100 sticky top-20 z-30">
        <div className="px-6 md:px-16 py-4 flex items-center gap-2 overflow-x-auto scrollbar-hide">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`whitespace-nowrap px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                activeCategory === cat
                  ? 'bg-[#2c2420] text-white'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              {cat}
            </button>
          ))}
          <span className="ml-auto text-xs text-stone-400 whitespace-nowrap pl-4 flex-shrink-0">
            {filtered.length} items
          </span>
        </div>
      </section>

      {/* Product Grid */}
      <section className="w-full bg-[#faf8f5] py-16 px-6 md:px-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map((product) => (
            <article
              key={product.id}
              className="bg-white rounded-2xl overflow-hidden group cursor-pointer"
              data-product-shop
              onClick={() => navigate(`/products/${product.id}`)}
            >
              <div className="relative overflow-hidden w-full h-[280px]">
                <img
                  src={product.image}
                  alt={product.name}
                  title={`${product.name} - premium sofa bed`}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                {product.tag && (
                  <span className="absolute top-3 left-3 bg-[#3a3228] text-white text-[10px] font-medium uppercase tracking-wider px-2.5 py-1 rounded-full">
                    {product.tag}
                  </span>
                )}
                {/* Add to Cart overlay */}
                <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      addItem({
                        id: product.id,
                        name: product.name,
                        price: product.price,
                        image: product.image,
                        category: product.category,
                      });
                    }}
                    className="whitespace-nowrap w-full py-3.5 bg-[#2c2420] text-white text-xs font-semibold uppercase tracking-widest hover:bg-[#8B6348] transition-colors cursor-pointer flex items-center justify-center gap-2"
                  >
                    <i className="ri-shopping-bag-line" />
                    Add to Cart
                  </button>
                </div>
              </div>

              <div className="p-5">
                <p className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">{product.category}</p>
                <h3 className="text-[#2c2420] font-semibold text-base mb-1.5" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {product.name}
                </h3>

                {/* Star Rating */}
                <div className="mb-2">
                  <StarRating
                    rating={product.rating}
                    reviewCount={product.reviewCount}
                    onClickReviews={(e) => {
                      e.stopPropagation();
                      setReviewProduct(product);
                    }}
                  />
                </div>

                <p className="text-stone-400 text-xs mb-3 leading-relaxed hidden sm:block">{product.description}</p>
                <div className="flex items-center justify-between">
                  <p className="text-[#8B6348] font-semibold text-base">${product.price.toLocaleString()}</p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      addItem({
                        id: product.id,
                        name: product.name,
                        price: product.price,
                        image: product.image,
                        category: product.category,
                      });
                    }}
                    className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-full bg-[#f5f0ea] text-[#8B6348] hover:bg-[#8B6348] hover:text-white transition-all duration-200 cursor-pointer"
                    title="Add to Cart"
                  >
                    <i className="ri-add-line text-base" />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <Footer />

      {/* Review Modal */}
      {reviewProduct && (
        <ReviewModal product={reviewProduct} onClose={() => setReviewProduct(null)} />
      )}
    </div>
  );
}
